import { createAsyncThunk, createSlice, current, ActionReducerMapBuilder } from "@reduxjs/toolkit";
import { PayloadAction } from "@reduxjs/toolkit";
import { compareKeys, generateAmazonClaimReferenceId } from "src/forms/tools";
import { ClaimSubmittedStatus } from "../types";
import {
  IntlProductLiabilityClaimSummary,
  ListInternationalProductLiabilityClaimsInput,
} from "@amzn/ttechclaimintakeservice-client";
import { listIPLClaimsService } from "src/api/IPL/listIPLClaims";
import { createIPLClaimService } from "src/api/IPL/createIPLClaim";
import {
  ProcessedApiResponse,
  ResponseField,
  SERVER_ERROR_RESPONSES,
  HttpStatusCodes,
} from "src/api/types";
import { IPL_TPA_SEDGWICK } from "src/forms/IPL/constants";
import { FormStateType } from "src/forms/types";
import { IPLClaimModelAdditionalFields } from "./types";

interface IPLAppState {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Index signature enables dynamic setValueInIPLAppState pattern
  [index: string]: any;
  submitScreenStatus: ClaimSubmittedStatus;
  iplClaimsList: IntlProductLiabilityClaimSummary[];
  fetchingClaimsList: boolean;
  listIPLClaimsError: string;
  createIPLClaimResponse?: ProcessedApiResponse;
  amazonClaimReferenceId: string;
  iplTpa: string;
}

export const initialState: IPLAppState = {
  submitScreenStatus: ClaimSubmittedStatus.LOADING,
  iplClaimsList: [],
  fetchingClaimsList: false,
  listIPLClaimsError: "",
  amazonClaimReferenceId: generateAmazonClaimReferenceId(),
  iplTpa: IPL_TPA_SEDGWICK,
};

export const verifyStateIsValid = (state: IPLAppState) => {
  return compareKeys(state, initialState);
};

export const listIPLClaims = createAsyncThunk(
  "iplApp/listIPLClaims",
  async (params: { input: ListInternationalProductLiabilityClaimsInput }, { rejectWithValue }) => {
    const { input } = params;
    try {
      const response = await listIPLClaimsService(input);
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const createIPLClaim = createAsyncThunk(
  "iplApp/createIPLClaim",
  async (
    params: {
      formState: FormStateType;
      additionalFields: IPLClaimModelAdditionalFields;
    },
    { rejectWithValue }
  ) => {
    const { formState, additionalFields } = params;
    try {
      const response = await createIPLClaimService(formState, additionalFields);
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const iplAppSlice = createSlice({
  name: "iplApp",
  initialState,
  reducers: {
    setValueInIPLAppState: (
      state: IPLAppState,
      action: PayloadAction<{
        key: string;
        value: IPLAppState[keyof IPLAppState];
      }>
    ) => {
      const { key, value } = action.payload;
      state[key as keyof IPLAppState] = value;
    },
    resetIPLAppState: () => {
      return {
        ...initialState,
        amazonClaimReferenceId: generateAmazonClaimReferenceId(),
      };
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<IPLAppState>) => {
    builder
      .addCase(listIPLClaims.fulfilled, (state: IPLAppState, action) => {
        state.iplClaimsList = action.payload?.claimsList ?? [];
        state.fetchingClaimsList = false;
        state.listIPLClaimsError = "";
      })
      .addCase(listIPLClaims.pending, (state: IPLAppState) => {
        state.fetchingClaimsList = true;
        state.listIPLClaimsError = "";
        state.iplClaimsList = [];
      })
      .addCase(listIPLClaims.rejected, (state: IPLAppState) => {
        state.fetchingClaimsList = false;
        state.listIPLClaimsError = "Error fetching IPL claims list";
        state.iplClaimsList = [];
      })
      .addCase(createIPLClaim.fulfilled, (state: IPLAppState, action) => {
        state.createIPLClaimResponse = action.payload ?? undefined;
        state.submitScreenStatus = ClaimSubmittedStatus.SUCCESS;
      })
      .addCase(createIPLClaim.pending, (state: IPLAppState) => {
        state.submitScreenStatus = ClaimSubmittedStatus.LOADING;
      })
      .addCase(createIPLClaim.rejected, (state: IPLAppState, action) => {
        state.submitScreenStatus = ClaimSubmittedStatus.ERROR;
        const data: ResponseField[] = [];
        const { message, errors, $metadata, warningList } = action.payload as Record<
          string,
          unknown
        >;
        const statusCode = ($metadata as Record<string, unknown>)?.httpStatusCode?.toString();
        if (SERVER_ERROR_RESPONSES.includes(statusCode as HttpStatusCodes)) {
          data.push({
            fieldName: "Amazon Claim Reference ID",
            fieldValue: current(state).amazonClaimReferenceId,
          });
        }

        if ((errors as ResponseField[])?.length) {
          data.push(...(errors as ResponseField[]));
        }
        const processedResponse: ProcessedApiResponse = {
          data,
          statusCode: statusCode as string | undefined,
          warningList: warningList as string[] | undefined,
          message: message as string | undefined,
        };
        state.createIPLClaimResponse = processedResponse;
      });
  },
});

export const { setValueInIPLAppState, resetIPLAppState } = iplAppSlice.actions;

export default iplAppSlice.reducer;
